import { HELP_CENTER_URL } from '@aloft/toolkit-shared';
import { Block, Button, Flex, ModeSwitcher, Typography } from '@aloft/toolkit-sui';

import { useState } from 'react';
import { Link } from 'react-router-dom';

// eslint-disable-next-line aloft/prefer-sound-ui-button
import { CraftsmanAddCompButtons } from '../../../components/CraftsmanAddCompButtons.tsx';
import { GuidedTourTooltip } from '../../../components/GuidedTour/GuidedTourTooltip.tsx';
import { useTkv2HandleIconClick } from '../../../components/ReferenceBar/useTkv2HandleIconClick.ts';
import { SettingsControls } from '../../../components/Settings/SettingsControls.tsx';
import { featuresSelectors } from '../../../redux/features/selectors/featuresSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { CostMultiplierModal } from '../CostMultiplierModal/CostMultiplierModal.tsx';
import { VIEW_MODES } from '../consts.ts';
import { ViewMode } from '../types.ts';

type AnalysisToolbarProps = {
  viewMode: ViewMode;
  setViewMode: (val: ViewMode) => void;
};
const { selectShouldShowFeatureGrid } = featuresSelectors;

export const AnalysisToolbar = ({ viewMode, setViewMode }: AnalysisToolbarProps) => {
  const [isCostMultiplierOpen, setIsCostMultiplierOpen] = useState(false);
  const handleIconClick = useTkv2HandleIconClick();
  const shouldShowFeatureGrid = useAppSelector(selectShouldShowFeatureGrid);

  return (
    <Flex
      className="toolbar-container"
      alignItems="center"
      gap="1.5rem"
      paddingLeft="1.5rem"
      paddingY={2}
      flexShrink={1}
      backgroundColor="surface1"
      flexWrap="wrap"
    >
      <GuidedTourTooltip
        text={[
          'Click on adjustments to view the results of your analysis. You can also view the adjustments in the Adjustment Grid or Commentary tabs.',
          'You can view the cost approach on the Cost Approach tab.',
        ]}
        tourID="analysisTour"
        tourIndex={10}
        placement="bottom"
      >
        <ModeSwitcher
          items={Object.entries(VIEW_MODES).map(([id, label]) => ({ id, label }))}
          value={viewMode}
          variant="secondary"
          size="sm"
          onChange={(newViewMode) => setViewMode(newViewMode as ViewMode)}
        />
      </GuidedTourTooltip>
      <Block width="1px" backgroundColor="strokeDefault" height="1rem" marginX="1rem" />
      {!shouldShowFeatureGrid && <CraftsmanAddCompButtons />}
      <Block>
        <Button
          variant="secondary"
          size="sm"
          text="Cost Multiplier"
          onClick={() => {
            setIsCostMultiplierOpen(true);
          }}
        />
        {isCostMultiplierOpen && (
          <CostMultiplierModal onClose={() => setIsCostMultiplierOpen(false)} />
        )}
      </Block>
      <Button
        variant="secondary"
        size="sm"
        text="Site Analysis"
        onClick={() => handleIconClick('site-analysis')}
      />
      <Link target="_blank" to={HELP_CENTER_URL}>
        <Typography variant="buttonRegular" color="buttonDefault">
          Quality Guide
        </Typography>
      </Link>
      <Block flexGrow={1} />
      <Block marginRight="1rem">
        <SettingsControls />
      </Block>
    </Flex>
  );
};
